import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSession } from '../../context/SessionContext';
import { useNavigation } from '../../hooks/useNavigation';
import { FAQ, StickyMenu } from '../ui';
import { CANDIDATE_ENDPOINTS } from '../../constants/apiEndpoints';
import toast from 'react-hot-toast';

function UploadResume() {
  const { eid, sessionData, isLoading, updateSessionData } = useSession();
  const { navigateNext, navigatePrevious } = useNavigation();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  
  // Pre-fill email from session data if available
  useEffect(() => {
    // Only use session data after loading is complete
    if (!isLoading && sessionData && sessionData.email) {
      setEmail(sessionData.email);
    }
  }, [sessionData, isLoading]);
  const [resume, setResume] = useState(null);
  const [resumeError, setResumeError] = useState('');
  const [additionalLink, setAdditionalLink] = useState('');
  const [linkError, setLinkError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [pendingNavigation, setPendingNavigation] = useState(false);
  
  // Set loading state based on session loading
  useEffect(() => {
    if (!isLoading) {
      setIsPageLoading(false);
    }
  }, [isLoading]);

  // Handle navigation when session data updates
  useEffect(() => {
    if (pendingNavigation && sessionData?.resumeReceived === true) {
      const navigationResult = navigateNext(location.pathname);
      
      if (!navigationResult) {
        setIsSubmitting(false);
      }
      
      setPendingNavigation(false);
    }
  }, [sessionData?.resumeReceived, pendingNavigation, navigateNext, location.pathname]);

  const handleSubmit = async () => {
    // Reset previous errors
    setEmailError('');
    setResumeError('');
    setLinkError('');
    
    let hasError = false;
    
    // Validate email
    if (!email) {
      setEmailError('Email address is required');
      hasError = true;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('Please enter a valid email address');
      hasError = true;
    }
    
    // Validate resume
    if (!resume) {
      setResumeError('Resume is required');
      hasError = true;
    }
    
    // Validate URL format before submission
    if (additionalLink && !validateUrl(additionalLink)) {
      setLinkError('Please enter a valid URL starting with http:// or https://');
      hasError = true;
    }
    
    if (hasError) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('resume', resume);
      formData.append('email', email);
      formData.append('link', additionalLink);

      // Determine action_data based on sessionData.is_job_evaluation
      let action_data = {};
      
      // Set action based on whether this is a job evaluation or talent assessment
      if (sessionData.is_job_evaluation) {
        action_data = {
          action: "create-job-profile-fit"
        };
      } else {
        action_data = {
          action: "generate-talent-profile-assessment"
        };
      }
      
      // Add action_data to form data
      formData.append('action_data', JSON.stringify(action_data));

      const url = `${CANDIDATE_ENDPOINTS.SUBMIT_RESUME}?eid=${eid}`;
      
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        // Parse the JSON response
        const responseData = await response.json();
        
        console.log("responseData upload resume:", responseData);

        // Check if the response has status = "ts_processing"
        if (responseData && responseData.status === "ts_processing") {
          // Update session data to mark resume as received
          console.log("Resume uploaded successfully, updating session data");
          const updateSuccess = updateSessionData({ resumeReceived: true });
          
          if (updateSuccess) {
            // Set flag to trigger navigation when session data updates
            console.log("Session updated successfully, waiting for React state to update");
            setPendingNavigation(true);
          } else {
            setIsSubmitting(false);
          }
        } else {
          // If status is not "ts_processing", show error
          setIsSubmitting(false);
          toast.error('Resume was not uploaded successfully. Please try again.', {
            duration: 5000,
            position: 'top-center',
          });
          console.error('Invalid response status:', responseData);
        }
      } else {
        setIsSubmitting(false);
        toast.error('Failed to upload resume. Please try again.', {
          duration: 5000,
          position: 'top-center',
        });
      }
    } catch (error) {
      console.error('Error uploading resume:', error);
      setIsSubmitting(false);
      toast.error('An error occurred while uploading your resume. Please try again.', {
        duration: 5000,
        position: 'top-center',
      });
    }
  };

  const handleNext = () => {
    // If resume is already on file and no new resume is selected, skip validation and server request
    if (sessionData && sessionData.resumeReceived && !resume) {
      // Navigate directly to the next page
      navigateNext(location.pathname);
    } else {
      // Otherwise, proceed with normal validation and submission
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    navigatePrevious(location.pathname);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.size > 10 * 1024 * 1024) {
        setResumeError('File size exceeds 10MB limit. Please upload a smaller file.');
        return;
      }
      setResume(file);
      setResumeError('');
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 10 * 1024 * 1024) {
        setResumeError('File size exceeds 10MB limit. Please upload a smaller file.');
        return;
      }
      setResume(file);
      setResumeError('');
    }
  };

  // Show loading indicator while session data is being loaded
  if (isPageLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent mb-4"></div>
          <p className="text-xl">Loading your information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen p-0 pt-20 pb-16 w-full">
      <StickyMenu />
      
      {/* 2-Column Layout Container */}
      <div className="flex-1 flex justify-center px-4">
        <div className="w-full max-w-[1100px] bg-white rounded-3xl shadow-md flex">
          {/* Left Column - Main Content */}
          <div className="flex-1 py-12 px-8 flex flex-col items-center border-r border-gray-200">
            <h2 className="text-3xl font-semibold mb-2 text-center">Please Share Your Info</h2>
            <p className="text-lg text-gray-600 mb-6 text-center px-12">
              Upload your Resume and optionally a link to your portfolio, GitHub, LinkedIn or similar.
            </p>

            <div className="w-full max-w-2xl mt-4 mb-8">
          <div className="mb-6">
            <label className="block mb-2">
              <span className="font-semibold">* E-mail Address</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError('');
              }}
              placeholder="email@address.com"
              className={`w-full p-3 border ${emailError ? 'border-red-500' : 'border-gray-300'} rounded`}
              required
            />
            {emailError && (
              <div className="bg-red-50 border-l-4 border-red-500 p-3 mt-1 rounded">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{emailError}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mb-6">
          <label className="block mb-2">
            <span className="font-semibold">* Resume</span>
            <span className="ml-1">(PDF only)</span>
            {sessionData && sessionData.resumeReceived && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#E6F9F7', color: '#0B7A70' }}>
                <svg className="-ml-0.5 mr-1.5 h-2 w-2" fill="currentColor" viewBox="0 0 8 8" style={{ color: '#33C6B6' }}>
                  <circle cx="4" cy="4" r="3" />
                </svg>
                On File
              </span>
            )}
          </label>
          <div
            className={`w-full h-44 border-2 border-dashed ${
              resumeError 
                ? 'border-red-500' 
                : resume 
                  ? 'border-blue-500' 
                  : sessionData && sessionData.resumeReceived
                    ? 'border-[#33C6B6]'
                    : 'border-gray-300'
            } rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors duration-200 ${
              resume 
                ? 'bg-blue-50' 
                : sessionData && sessionData.resumeReceived && !resume
                  ? 'bg-[#E6F9F7]'
                  : 'bg-gray-50'
            }`}
            onDragOver={handleDragOver}
            onDrop={(e) => {
              handleDrop(e);
              if (resumeError) setResumeError('');
            }}
            onClick={() => document.getElementById('resume-upload').click()}
          >
            {resume ? (
              <div className="flex flex-col items-center justify-center w-full h-full p-4">
                <div className="mb-3 text-blue-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-blue-700 font-medium text-center">{resume.name}</p>
                <p className="text-blue-500 text-sm mt-2">
                  {(resume.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>
            ) : sessionData && sessionData.resumeReceived && !resume ? (
              <div className="flex flex-col items-center">
                <div className="mb-3" style={{ color: '#33C6B6' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="font-medium" style={{ color: '#0B7A70' }}>Resume already on file</p>
                <p className="text-sm mt-1" style={{ color: '#1A9D92' }}>Upload a new one or continue to the next step</p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div className="mb-2 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <p className="text-gray-700 font-medium">Upload a file</p>
                <p className="text-gray-500 text-sm mt-1">or drag and drop</p>
                <p className="text-gray-500 text-xs mt-2">PDF up to 10MB</p>
              </div>
            )}
            <input
              id="resume-upload"
              type="file"
              accept=".pdf"
              onChange={(e) => {
                handleFileSelect(e);
                if (resumeError) setResumeError('');
              }}
              className="hidden"
            />
          </div>
          {resumeError && (
            <div className="bg-red-50 border-l-4 border-red-500 p-3 mt-1 rounded">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{resumeError}</p>
                </div>
              </div>
            </div>
          )}
        </div>

          <div className="mb-8">
          <label className="block mb-2">
            <span className="font-semibold">Additional Link</span>
            <span className="ml-1">(e.g. LinkedIn, Portfolio, GitHub)</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="url"
              value={additionalLink}
              onChange={(e) => {
                const value = e.target.value;
                // If value is empty, just set it as is
                if (!value) {
                  setAdditionalLink('');
                  setLinkError('');
                  return;
                }
                
                // If value doesn't start with http:// or https://, prepend https://
                if (!value.match(/^https?:\/\//i)) {
                  setAdditionalLink(`https://${value}`);
                } else {
                  setAdditionalLink(value);
                }
                
                // Clear error if URL is valid
                if (validateUrl(value) || validateUrl(`https://${value}`)) {
                  setLinkError('');
                }
              }}
              onBlur={() => {
                // Validate on blur
                if (additionalLink && !validateUrl(additionalLink)) {
                  setLinkError('Please enter a valid URL starting with http:// or https://');
                } else {
                  setLinkError('');
                }
              }}
              placeholder="https://myportfolio.com/"
              className={`w-full p-3 pl-10 border ${linkError ? 'border-red-500' : 'border-gray-300'} rounded`}
            />
          </div>
          {linkError && (
            <div className="bg-red-50 border-l-4 border-red-500 p-3 mt-1 rounded">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{linkError}</p>
                </div>
              </div>
            </div>
          )}
        </div>

              <div className="flex justify-between w-full max-w-2xl mb-6">
                <button
                  onClick={handlePrevious}
                  className="bg-white text-gray-700 border-2 border-gray-600 rounded-md px-6 py-4 text-xl flex items-center hover:bg-gray-50 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                <button
                  onClick={handleNext}
                  disabled={isSubmitting}
                  className={`text-white rounded-md px-6 py-4 text-xl ${
                    isSubmitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="inline-block animate-spin mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : sessionData && sessionData.resumeReceived && !resume ? (
                    "Continue"
                  ) : (
                    "Upload & Continue"
                  )}
                </button>
              </div>
            </div>
            
            {/* FAQ Section */}
            <FAQ faqType="resume" />
          </div>

          {/* Right Column - Sidebar */}
          <div 
            className="w-80 py-10 px-8 rounded-r-3xl flex flex-col justify-between relative" 
            style={{ 
              backgroundColor: '#F9F9F9',
              backgroundImage: 'url(/leftbg.svg), url(/hotcircle.svg)',
              backgroundSize: '120% auto, auto',
              backgroundRepeat: 'no-repeat, no-repeat',
              backgroundPosition: 'top center, bottom center'
            }}
          >
            <div>
              <h3 className="text-2xl font-regular text-gray-900 mb-6 leading-tight">
                About Your Resume
              </h3>

              <div className="space-y-4">
                {/* Tips for a good resume */}
                <div className="py-2">
                  <div className="flex items-start">
                    <span className="flex-shrink-0 w-2 h-2 bg-teal-500 rounded-full mt-2 mr-3"></span>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Tips for a good resume</h4>
                      <p className="text-sm text-gray-700">
                        Ensure your resume is complete, up-to-date, and highlights your relevant skills and experience clearly.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Data privacy */}
                <div className="py-2">
                  <div className="flex items-start">
                    <span className="flex-shrink-0 w-2 h-2 bg-teal-500 rounded-full mt-2 mr-3"></span>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Data privacy</h4>
                      <p className="text-sm text-gray-700">
                        Your information is secure and will only be used for evaluation purposes. We never share your data without consent.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-300 pt-4">
              <div className="text-xs text-gray-500 text-center">
                Powered by tendent.ai
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper function to validate URL format
const validateUrl = (url) => {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch (e) {
    return false;
  }
};

export default UploadResume;
